import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import attendanceService from "../../services/attendanceService";
import { getTeacherClasses } from "../../services/teacherService";
import classSubjectService from "../../services/classSubjectService";
import QuickMarkAttendance from "./QuickMarkAttendance";
import { useDepartments } from "../../hooks/queries/useDepartments";
import { useClasses } from "../../hooks/queries/useCourses";

const toYmd = (v) => {
  const d = v ? new Date(v) : new Date();
  const s = Number.isNaN(d.getTime()) ? new Date() : d;
  return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, "0")}-${String(s.getDate()).padStart(2, "0")}`;
};
const normalizeClasses = (p) => {
  const d = p?.data ?? p;
  const l =
    d?.data?.classData ||
    d?.classData ||
    d?.data?.rows ||
    d?.rows ||
    d?.data ||
    d ||
    [];
  return Array.isArray(l)
    ? l.map((c) => ({
        id: c.id ?? c.classId,
        name: c.name || c.className || `Class ${c.id ?? c.classId}`,
        section: c.section ?? c.classSection ?? "",
        practicalBatches:
          c.practicalBatches ??
          c.practical_batches ??
          c.practicalBatchCodes ??
          c.practical_batch_codes ??
          [],
      }))
    : [];
};
const normalizeSubjects = (p) => {
  const d = p?.data ?? p;
  const l = d?.data?.subjects || d?.subjects || d?.data || [];
  return Array.isArray(l)
    ? l.map((s) => ({
        id: s.id || s.subjectId,
        name: s.name || s.subjectName || `Subject ${s.id}`,
      }))
    : [];
};
const classLabel = (c) => {
  const n = c?.name || "";
  const s = c?.section ?? "";
  return s ? `${n} - ${s}` : n || "—";
};

/**
 * Normalize the RAW axios response returned by `useDepartments` (pass-through hook).
 * Shape can be response.data.data (array), response.data (array), or response.data.departments.
 * Always returns a guarded array (no-crash rule 5).
 */
const normalizeDepartmentsResponse = (payload) => {
  const rows =
    payload?.data?.data ??
    payload?.data?.departments ??
    payload?.data ??
    [];
  const list = Array.isArray(rows) ? rows : Array.isArray(rows?.departments) ? rows.departments : [];
  return list.map((d) => ({
    id: d?.id ?? d?.departmentId ?? d?._id,
    name: d?.name || d?.departmentName || `Department ${d?.id ?? d?.departmentId ?? ""}`,
  }));
};

/**
 * Normalize the RAW axios response returned by `useClasses` (admin-scoped class list).
 * Distinct from the teacher endpoint shape handled by `normalizeClasses`.
 */
const normalizeAdminClassesResponse = (payload) => {
  const rows =
    payload?.data?.data?.classData ??
    payload?.data?.classData ??
    payload?.data?.classes ??
    payload?.data?.data ??
    payload?.data ??
    [];
  const list = Array.isArray(rows) ? rows : [];
  return list.map((c) => ({
    id: c?.id ?? c?.classId,
    name: c?.name || c?.className || `Class ${c?.id ?? c?.classId}`,
    section: c?.section ?? c?.classSection ?? c?.sectionName ?? "",
    practicalBatches:
      c?.practicalBatches ??
      c?.practical_batches ??
      c?.practicalBatchCodes ??
      c?.practical_batch_codes ??
      [],
  }));
};

const statusColor = (status) => {
  if (status === "success") return "success";
  if (status === "failed") return "error";
  return "warning";
};

export default function ManualAttendanceUpload() {
  const authUser = useSelector((s) => s.auth?.user);
  const rawRole = String(authUser?.role || "").trim().toLowerCase();
  const userRole = rawRole.replace(/[_\s]+/g, "") === "superadmin" ? "superadmin" : rawRole;
  const restrictToAssignments = userRole === "teacher" || userRole === "hod";
  // Admin / institute / super-admin viewers get a Department dropdown that scopes
  // the Class dropdown (Req 4.1-4.3). Teacher/HOD keep the assigned-class path (Req 4.4).
  const isAdminOrInstitute = ["admin", "institute", "superadmin"].includes(userRole);
  // Institute context: super admin scopes an institute via the Redux instituteContext
  // selectedInstitute (dropdown-select flow); everyone else is bound to their own institute.
  const selectedInstitute = useSelector((s) => s.instituteContext?.selectedInstitute);
  const instituteId =
    (userRole === "superadmin" ? selectedInstitute?.id : null) ??
    authUser?.instituteId ??
    selectedInstitute?.id ??
    null;
  // Admin/SA pick a department to scope classes; the value is cleared here (not locked)
  // because teacher/HOD never render this dropdown.
  const [departmentId, setDepartmentId] = useState("");

  // Department options: only fetched/rendered for admin/super-admin viewers.
  const { data: departmentsResponse } = useDepartments(
    isAdminOrInstitute && instituteId ? { instituteId } : {},
  );
  const departments = useMemo(
    () => (isAdminOrInstitute ? normalizeDepartmentsResponse(departmentsResponse) : []),
    [isAdminOrInstitute, departmentsResponse],
  );

  // Admin/SA class list is scoped by the chosen department, only requested once a
  // department is picked (Req 4.3 — no unbounded all-department fetch).
  const adminClassFilters = useMemo(() => {
    if (!isAdminOrInstitute) return null;
    if (!instituteId || !departmentId) return null;
    return { instituteId, departmentId: Number(departmentId) };
  }, [isAdminOrInstitute, instituteId, departmentId]);
  const { data: adminClassesResponse } = useClasses(adminClassFilters || {});
  const adminScopedClasses = useMemo(
    () => (adminClassFilters ? normalizeAdminClassesResponse(adminClassesResponse) : []),
    [adminClassFilters, adminClassesResponse],
  );

  const [activeTab, setActiveTab] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [logDetail, setLogDetail] = useState(null);
  const [classes, setClasses] = useState([]),
    [subjects, setSubjects] = useState([]),
    [date, setDate] = useState(toYmd(new Date())),
    [classId, setClassId] = useState(""),
    [subjectId, setSubjectId] = useState(""),
    [batch, setBatch] = useState(""),
    [classBatches, setClassBatches] = useState([]),
    [sessions, setSessions] = useState([]),
    [loading, setLoading] = useState(false),
    [uploading, setUploading] = useState(false),
    [error, setError] = useState(""),
    [file, setFile] = useState(null),
    [fileInputKey, setFileInputKey] = useState(0),
    [selectedSlot, setSelectedSlot] = useState(null),
    [result, setResult] = useState(null);
  const [markMode, setMarkMode] = useState("quick"); // "quick" or "csv"
  // Subject is OPTIONAL: fetching only requires a date + class. When no subject is
  // picked the backend returns ALL lectures of that class on the date (role-scoped).
  const canFetch = useMemo(
    () => !!(date && classId),
    [date, classId],
  );
  const {
    data: classesRes,
    isLoading: classesLoading,
    error: classesError,
  } = useQuery({
    queryKey: ["teacher-class-names", restrictToAssignments ? "assigned" : "all"],
    // Admin/Super-admin viewers source classes from the department-scoped useClasses
    // hook, so the teacher-only endpoint is skipped for them.
    enabled: !isAdminOrInstitute,
    queryFn: async () => {
      const r = await getTeacherClasses(
        restrictToAssignments ? { assignedOnly: true, forAttendance: true } : {}
      );
      return r;
    },
    staleTime: 0,
  });
  useEffect(() => {
    if (classesRes) {
      setClasses(normalizeClasses(classesRes));
    }
  }, [classesRes]);

  // For admin/super-admin viewers the effective class options come from the
  // department-scoped useClasses response; for teacher/HOD they come from state.
  const effectiveClasses = isAdminOrInstitute ? adminScopedClasses : classes;

  // When the admin/SA viewer changes department, clear a stale class/subject pick.
  useEffect(() => {
    if (!isAdminOrInstitute) return;
    setClassId("");
    setSubjectId("");
    setBatch("");
    setClassBatches([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, isAdminOrInstitute]);
  useEffect(() => {
    if (classesError) {
      setError(
        classesError?.response?.data?.message ||
          classesError?.message ||
          "Failed to load classes",
      );
    }
  }, [classesError]);
  useEffect(() => {
    if (!classId) {
      setSubjects([]);
      setSubjectId("");
      setBatch("");
      setClassBatches([]);
      return;
    }
  }, [classId]);
  useEffect(() => {
    if (!classId) return;
    const selected = (effectiveClasses || []).find(
      (c) => String(c.id) === String(classId),
    );
    const batchesRaw = selected?.practicalBatches || [];
    const list = Array.isArray(batchesRaw)
      ? batchesRaw
      : typeof batchesRaw === "string"
        ? batchesRaw.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
    const normalized = [...new Set(list.map((b) => String(b).trim()).filter(Boolean))];
    setClassBatches(normalized);
    setBatch("");
  }, [classId, effectiveClasses]);
  const {
    data: subjectsRes,
    isLoading: subjectsLoading,
    error: subjectsError,
  } = useQuery({
    queryKey: ["class-subjects", String(classId)],
    enabled: !!classId,
    queryFn: async () => {
      const r = await classSubjectService.getSubjectsByClass(classId);
      return r;
    },
    staleTime: 0,
  });
  useEffect(() => {
    if (!classId) return;
    if (subjectsRes) {
      const s = normalizeSubjects(subjectsRes);
      setSubjects(s);
      if (!s.find((x) => String(x.id) === String(subjectId))) setSubjectId("");
    }
  }, [classId, subjectsRes, subjectId]);
  useEffect(() => {
    if (subjectsError) {
      setSubjects([]);
      setSubjectId("");
      setError(
        subjectsError?.response?.data?.message ||
          subjectsError?.message ||
          "Failed to load subjects",
      );
    }
  }, [subjectsError]);

  const {
    data: logsRes,
    isLoading: logsLoading,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: ["attendance-upload-logs", logsPage],
    queryFn: async () => {
      const r = await attendanceService.getAttendanceUploadLogs({ page: logsPage, limit: 20 });
      return r?.data?.data || r?.data || { rows: [], pagination: {} };
    },
    enabled: activeTab === 1,
    staleTime: 0,
  });

  const uploadLogs = logsRes?.rows || [];
  const logsPagination = logsRes?.pagination || {};

  const openLogDetail = async (logId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setLogDetail(null);
    try {
      const r = await attendanceService.getAttendanceUploadLogById(logId);
      setLogDetail(r?.data?.data || r?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load log details");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadErrorReport = async (logId) => {
    try {
      const r = await attendanceService.downloadAttendanceUploadLogErrors(logId);
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_upload_errors_${logId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to download error report");
    }
  };

  const fetchSessions = async () => {
    if (!canFetch) return;
    setError("");
    setResult(null);
    setSelectedSlot(null);
    try {
      setLoading(true);
      const r = await attendanceService.getManualUploadSessions({
        date,
        classId,
        // Subject is optional: omit the param entirely when unselected so the
        // backend returns all class lectures (axios drops undefined keys).
        subjectId: subjectId || undefined,
        practicalBatchCode: batch || undefined,
      });
      setSessions(r?.data?.data || []);
    } catch (e) {
      setSessions([]);
      setError(
        e?.response?.data?.message || e?.message || "Failed to fetch sessions",
      );
    } finally {
      setLoading(false);
    }
  };
  const downloadSample = async () => {
    setError("");
    try {
      const r = await attendanceService.downloadManualUploadSampleCsv();
      const blob = new Blob([r.data], {
        type: r.headers?.["content-type"] || "text/csv",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "manual_attendance_upload_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to download sample CSV",
      );
    }
  };
  const onUpload = async () => {
    if (!selectedSlot || !file) {
      setError("Select a lecture and choose a CSV file");
      return;
    }
    // Subject may be left blank at the top filter. The chosen lecture row carries
    // its OWN subjectId (from the backend session response), so prefer that and
    // only fall back to the top-level filter. Never upload an empty subjectId.
    const uploadSubjectId =
      selectedSlot?.subjectId ?? selectedSlot?.subject?.id ?? subjectId;
    if (uploadSubjectId == null || String(uploadSubjectId).trim() === "") {
      setError("Selected lecture has no subject. Pick a lecture with a subject or choose a subject.");
      return;
    }
    setError("");
    setResult(null);
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("date", date);
      fd.append("timeTableSlotId", String(selectedSlot.timeTableSlotId));
      fd.append("classId", String(classId));
      fd.append("subjectId", String(uploadSubjectId));
      // Re-upload replaces ONLY previous MANUAL attendance for this lecture (QR/OTP unchanged).
      fd.append("preventOverwrite", "false");
      fd.append("file", file);
      const r = await attendanceService.uploadManualAttendanceCsv(fd);
      const data = r?.data?.data || null;
      setResult(data);
      const rr = data?.results || {};
      const sc = rr?.success?.length || 0;
      const fc = rr?.failed?.length || 0;
      const kc = rr?.skipped?.length || 0;
      if (fc > 0) {
        const first = rr.failed?.[0];
        const firstMsg = first?.reason ? ` First error: ${first.reason}` : "";
        toast.error(`Upload completed with errors. Success: ${sc}, Failed: ${fc}, Skipped: ${kc}.${firstMsg}`);
      } else {
        toast.success(`Upload successful. Success: ${sc}, Skipped: ${kc}.`);
      }
      await fetchSessions();
      if (activeTab === 1) refetchLogs();
      setFile(null);
      setFileInputKey((k) => k + 1);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Upload failed");
      toast.error(e?.response?.data?.message || e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Manual Attendance Upload
      </Typography>
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="Upload Attendance" />
        <Tab label="Upload Logs" />
      </Tabs>
      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      ) : null}

      {activeTab === 0 ? (
      <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Date"
                format={DATE_DISPLAY_FORMAT}
                value={date ? new Date(date) : null}
                onChange={(val) => setDate(toApiDate(val))}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            {isAdminOrInstitute ? (
              <Grid
                item
                xs={12}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    label="Department"
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                  >
                    {(departments || []).map((d) => (
                      <MenuItem key={d.id} value={String(d.id)}>
                        {d.name}
                      </MenuItem>
                    ))}
                    {!(departments || []).length ? (
                      <MenuItem value="" disabled>
                        No departments found
                      </MenuItem>
                    ) : null}
                  </Select>
                </FormControl>
              </Grid>
            ) : null}
            <Grid
              item
              xs={12}
              md={3}
            >
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  label="Class"
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                  disabled={isAdminOrInstitute && !departmentId}
                >
                  {isAdminOrInstitute && !departmentId ? (
                    <MenuItem value="" disabled>
                      Select department first
                    </MenuItem>
                  ) : null}
                  {!isAdminOrInstitute && classesLoading ? (
                    <MenuItem value="" disabled>
                      Loading...
                    </MenuItem>
                  ) : null}
                  {(effectiveClasses || []).map((c) => (
                    <MenuItem
                      key={c.id}
                      value={c.id}
                    >
                      {classLabel(c)}
                    </MenuItem>
                  ))}
                  {(!isAdminOrInstitute || departmentId) &&
                  !classesLoading &&
                  !(effectiveClasses || []).length ? (
                    <MenuItem value="" disabled>
                      No options found
                    </MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <FormControl fullWidth>
                <InputLabel>Subject (optional)</InputLabel>
                <Select
                  label="Subject (optional)"
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                >
                  {!classId ? (
                    <MenuItem value="" disabled>
                      Select class first
                    </MenuItem>
                  ) : null}
                  {subjectsLoading ? (
                    <MenuItem value="" disabled>
                      Loading...
                    </MenuItem>
                  ) : subjects.length ? (
                    subjects.map((s) => (
                      <MenuItem
                        key={s.id}
                        value={s.id}
                      >
                        {s.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No options found
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <FormControl fullWidth>
                <InputLabel>Batch (optional)</InputLabel>
                <Select
                  label="Batch (optional)"
                  value={batch}
                  onChange={(e) => setBatch(e.target.value)}
                  disabled={!classId}
                >
                  {!classId ? (
                    <MenuItem value="" disabled>
                      Select class first
                    </MenuItem>
                  ) : (
                    <MenuItem value="">
                      All / No batch filter
                    </MenuItem>
                  )}
                  {classBatches.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                  {!!classId && classBatches.length === 0 ? (
                    <MenuItem value="" disabled>
                      No batches configured for this class
                    </MenuItem>
                  ) : null}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Button
                  variant="contained"
                  onClick={fetchSessions}
                  disabled={!canFetch || loading}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {loading ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null}
                  Fetch Scheduled Lectures
                </Button>
                <Button
                  variant="outlined"
                  onClick={downloadSample}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Download Sample CSV
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ mb: 1 }}
          >
            Scheduled Lectures
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Marked</TableCell>
                    <TableCell>Manual Uploaded</TableCell>
                    <TableCell>Last Manual Upload</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Select</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(sessions || []).map((s) => (
                    <TableRow
                      key={`${s.timeTableSlotId}-${s.date}`}
                      selected={
                        selectedSlot?.timeTableSlotId === s.timeTableSlotId
                      }
                    >
                      <TableCell>{s.date}</TableCell>
                      <TableCell>
                        {s.startTime} - {s.endTime}
                      </TableCell>
                      <TableCell>{s.practicalBatchCode || "—"}</TableCell>
                      <TableCell>
                        {String(s.attendanceMarkedCount || 0)}
                        {s.isQrOtpFinalized && (
                          <Chip label="QR/OTP" size="small" color="success" sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>{s.manualUploadDone ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {s.lastManualUploadAt
                          ? `${s.lastManualUploadByName || s.lastManualUploadBy || "Unknown"} • ${new Date(
                              s.lastManualUploadAt,
                            ).toLocaleString()}`
                          : "—"}
                      </TableCell>
                      <TableCell>{String(s.manualUploadVersion || 0)}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={
                            selectedSlot?.timeTableSlotId === s.timeTableSlotId
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() => {
                            setSelectedSlot(s);
                            if (s.isQrOtpFinalized) setMarkMode("quick");
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!sessions || sessions.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                      >
                        No scheduled lectures found
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {/* Mode toggle: Quick Mark vs CSV Upload */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Marking Mode:
        </Typography>
        <ToggleButtonGroup
          value={markMode}
          exclusive
          onChange={(_, v) => { if (v) setMarkMode(v); }}
          size="small"
        >
          <ToggleButton value="quick">Quick Mark</ToggleButton>
          <ToggleButton value="csv">CSV Upload</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {markMode === "quick" ? (
        <QuickMarkAttendance
          selectedSlot={selectedSlot}
          classId={classId}
          subjectId={subjectId}
          date={date}
          batch={batch}
          onUploadComplete={fetchSessions}
        />
      ) : selectedSlot?.isQrOtpFinalized ? (
      <Card>
        <CardContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This lecture already has QR/OTP attendance ({selectedSlot.attendanceMarkedCount || 0} students marked).
            CSV upload is not available for lectures with existing QR/OTP attendance.
            Use <b>Quick Mark</b> mode to mark remaining students.
          </Alert>
        </CardContent>
      </Card>
      ) : (
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ mb: 1 }}
          >
            Upload CSV
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Re-upload is supported. The latest CSV will replace only the previous <b>manual</b> attendance snapshot for
            the selected lecture. QR/OTP attendance is not modified.
          </Alert>
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                value={
                  selectedSlot
                    ? `${selectedSlot.date} | ${selectedSlot.startTime}-${selectedSlot.endTime} | Slot ${selectedSlot.timeTableSlotId}`
                    : ""
                }
                label="Selected Lecture"
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
            >
              <Button
                variant="outlined"
                component={uploading ? "div" : "label"}
                disabled={!selectedSlot || uploading}
              >
                {uploading ? "Processing..." : file ? file.name : "Choose CSV"}
                <input
                  key={fileInputKey}
                  hidden
                  type="file"
                  accept=".csv,text/csv"
                  disabled={uploading}
                  onChange={(e) => {
                    if (uploading) {
                      e.target.value = "";
                      return;
                    }
                    setFile(e.target.files?.[0] || null);
                  }}
                />
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              md={2}
            >
              <Button
                variant="contained"
                onClick={onUpload}
                disabled={!selectedSlot || !file || uploading}
              >
                {uploading ? (
                  <CircularProgress
                    size={20}
                    sx={{ mr: 1 }}
                  />
                ) : null}
                Upload
              </Button>
            </Grid>
          </Grid>
          {result?.results ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Upload completed successfully.
              </Alert>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">
                        Success
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {result.results.success?.length || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">
                        Skipped
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        {result.results.skipped?.length || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="caption" color="text.secondary">
                        Failed
                      </Typography>
                      <Typography variant="h6" color="error.main">
                        {result.results.failed?.length || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              {(result.results.failed || []).length ? (
                <TableContainer sx={{ mt: 2, overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Identifier</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.results.failed.map((f, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{f.rowNumber}</TableCell>
                          <TableCell>{f.identifier || "—"}</TableCell>
                          <TableCell>{f.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Box>
          ) : null}
        </CardContent>
      </Card>
      )}
      </>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload History
            </Typography>
            {logsLoading ? (
              <CircularProgress />
            ) : (
              <>
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Upload Date & Time</TableCell>
                        <TableCell>Uploaded By</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Section</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>File Name</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Success</TableCell>
                        <TableCell align="right">Failed</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            {log.uploadDate
                              ? new Date(log.uploadDate).toLocaleString()
                              : "—"}
                          </TableCell>
                          <TableCell>{log.uploadedBy || "—"}</TableCell>
                          <TableCell>{log.role || "—"}</TableCell>
                          <TableCell>{log.className || log.classId || "—"}</TableCell>
                          <TableCell>{log.section || "—"}</TableCell>
                          <TableCell>{log.subjectName || "—"}</TableCell>
                          <TableCell>{log.fileName || "—"}</TableCell>
                          <TableCell align="right">{log.totalRecords ?? 0}</TableCell>
                          <TableCell align="right">{log.successfulRecords ?? 0}</TableCell>
                          <TableCell align="right">{log.failedRecords ?? 0}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={String(log.status || "").toUpperCase()}
                              color={statusColor(log.status)}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openLogDetail(log.id)}
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!uploadLogs.length ? (
                        <TableRow>
                          <TableCell colSpan={12} align="center">
                            No upload logs yet
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    disabled={logsPage <= 1}
                    onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: "center" }}>
                    Page {logsPagination.page || logsPage} of {logsPagination.totalPages || 1}
                  </Typography>
                  <Button
                    size="small"
                    disabled={
                      (logsPagination.totalPages || 1) <= (logsPagination.page || logsPage)
                    }
                    onClick={() => setLogsPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Log Details</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <CircularProgress />
          ) : logDetail ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>File:</strong> {logDetail.fileName || "—"} |{" "}
                <strong>Class:</strong> {logDetail.className} ({logDetail.section || "—"}) |{" "}
                <strong>Subject:</strong> {logDetail.subjectName || "—"}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Success: {logDetail.successfulRecords ?? 0} | Failed: {logDetail.failedRecords ?? 0}{" "}
                | Skipped: {logDetail.skippedRecords ?? 0}
              </Typography>
              {(logDetail.failedRows || []).length > 0 ? (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Failed / Skipped Rows
                  </Typography>
                  <TableContainer sx={{ maxHeight: 240, mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Identifier</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...(logDetail.failedRows || []), ...(logDetail.skippedRows || [])].map(
                          (row, idx) => (
                            <TableRow key={`${row.id}-${idx}`}>
                              <TableCell>{row.rowNumber}</TableCell>
                              <TableCell>{row.identifier || row.rollNumber || "—"}</TableCell>
                              <TableCell>{row.status}</TableCell>
                              <TableCell>{row.errorMessage || "—"}</TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
              {(logDetail.successRows || []).length > 0 ? (
                <>
                  <Typography variant="subtitle2">Successful Rows</Typography>
                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Identifier</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logDetail.successRows.map((row, idx) => (
                          <TableRow key={`ok-${row.id}-${idx}`}>
                            <TableCell>{row.rowNumber}</TableCell>
                            <TableCell>{row.identifier || row.rollNumber || "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
            </Box>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "nowrap", gap: 1, px: 3, pb: 2 }}>
          {logDetail?.id && (logDetail.failedRecords > 0 || logDetail.skippedRecords > 0) ? (
            <Button
              variant="outlined"
              onClick={() => downloadErrorReport(logDetail.id)}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Download Error Report
            </Button>
          ) : null}
          <Button onClick={() => setDetailOpen(false)} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
